import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import LoadingSpinner from '../loading/LoadingSpinner';
import { api } from '../../api';
import { useLocale } from '../../context';
import { useSiteContent } from '../../context/SiteContentContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import WelcomeBannerMobile from '../mobile/WelcomeBannerMobile';
import { WelcomeBannerSkeleton } from './Skeleton';
import FeaturedMatchSlide from './FeaturedMatchSlide';
import {
  interleaveMatchSlides,
  pickFeaturedFootballMatches,
} from './featuredMatchSlides';

const FEATURED_POLL_MS = 20000;
const FEATURED_LIMIT = 17;
const FEATURED_FLASH_MS = 1800;

function BannerDot({ active, duration, paused, onClick, label, current }) {
  return (
    <button
      type="button"
      className={`welcome-banner__dot${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-label={label}
      aria-current={current ? 'true' : undefined}
    >
      <svg className="welcome-banner__dot-svg" viewBox="0 0 12 12" aria-hidden="true">
        <circle className="welcome-banner__dot-core" cx="6" cy="6" r="3" />
        <circle
          className={`welcome-banner__dot-progress${active ? ' is-active' : ''}`}
          cx="6"
          cy="6"
          r="5"
          pathLength="100"
          style={active ? { animationDuration: `${duration}s`, animationPlayState: paused ? 'paused' : 'running' } : undefined}
        />
      </svg>
    </button>
  );
}

function preloadSlideImage(src, onLoad) {
  if (!src) return;
  const img = new Image();
  img.decoding = 'async';
  img.onload = onLoad;
  img.onerror = onLoad;
  img.src = src;
  if (img.complete && img.naturalWidth > 0) onLoad();
}

const DEFAULT_GLOW = 'rgba(18,20,28,0.5)';
const glowColorCache = new Map();
function sampleBannerColor(rawUrl) {
  if (!rawUrl) return Promise.resolve(null);
  let url = rawUrl;
  try { url = url.replace(/^https?:\/\/[^\/]+\/(uploads|game-icons)\//, '/$1/'); } catch {}
  if (glowColorCache.has(url)) return Promise.resolve(glowColorCache.get(url));
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = 24, h = 12;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) {
          const pr = d[i], pg = d[i + 1], pb = d[i + 2], pa = d[i + 3] / 255;
          if (pa < 0.1) continue;
          const mx = Math.max(pr, pg, pb), mn = Math.min(pr, pg, pb);
          const sat = mx > 0 ? (mx - mn) / mx : 0;
          const wt = sat * sat * (mx / 255) * pa;
          r += pr * wt; g += pg * wt; b += pb * wt; n += wt;
        }
        if (!n) { glowColorCache.set(url, null); return resolve(null); }
        r = Math.round(r / n); g = Math.round(g / n); b = Math.round(b / n);
        const mx = Math.max(r, g, b, 1);
        const target = 185;
        if (mx < target) { const s = target / mx; r = Math.min(255, Math.round(r * s)); g = Math.min(255, Math.round(g * s)); b = Math.min(255, Math.round(b * s)); }
        const col = `rgb(${r}, ${g}, ${b})`;
        glowColorCache.set(url, col);
        resolve(col);
      } catch {
        glowColorCache.set(url, null);
        resolve(null);
      }
    };
    img.onerror = () => { glowColorCache.set(url, null); resolve(null); };
    img.src = url;
  });
}

function useSlideClick({ onNavigate, onPromoDetails, onWelcomeLanding, getPromoByPromoId }) {
  return useCallback((item) => {
    if (!item) return;
    if (item.kind === 'match') return;

    const action = String(item.ctaAction || item.linkType || '').trim();
    const promoId = item.promoId || null;

    if ((action === 'promo' || promoId) && promoId) {
      const promo = getPromoByPromoId?.(promoId);
      if (promo) {
        onPromoDetails?.(promo);
        return;
      }
    }

    if (action === 'welcome-landing') {
      onWelcomeLanding?.();
      return;
    }

    const href = item.href || (
      action.startsWith('http://') || action.startsWith('https://') || action.startsWith('/')
        ? action
        : null
    );
    if (href) {
      if (href.startsWith('/')) {
        window.history.pushState({}, '', href);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (action && !['none', 'promo', 'url', ''].includes(action)) {
      onNavigate?.(action);
    }
  }, [getPromoByPromoId, onNavigate, onPromoDetails, onWelcomeLanding]);
}

function oddKey(matchId, label) {
  return `${matchId}:${label}`;
}

function withFeaturedTicks(nextList, prevOddsMap) {
  const nextOddsMap = new Map();
  const stamped = (Array.isArray(nextList) ? nextList : []).map((ev) => {
    const odds = (Array.isArray(ev.odds) ? ev.odds : []).map((odd) => {
      const key = oddKey(ev.id, odd.label);
      const nextVal = Number(odd.value);
      const prevVal = prevOddsMap.get(key);
      nextOddsMap.set(key, nextVal);
      let tick = null;
      if (
        Number.isFinite(prevVal)
        && Number.isFinite(nextVal)
        && nextVal > 0
        && Math.abs(nextVal - prevVal) >= 0.001
      ) {
        tick = nextVal > prevVal ? 'up' : 'down';
      }
      return { ...odd, tick };
    });
    return { ...ev, odds };
  });
  return { stamped, nextOddsMap };
}

function useFeaturedFootball(locale) {
  const [matches, setMatches] = useState([]);
  const idsRef = useRef('');
  const prevOddsRef = useRef(new Map());
  const flashTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await api.nexusTopMatches({ lang: locale === 'en' ? 'en' : 'tr', banners: true });
        const picked = pickFeaturedFootballMatches(data?.matches, FEATURED_LIMIT);
        if (!alive) return;
        const ids = picked.map((m) => m.id).join(',');
        const { stamped, nextOddsMap } = withFeaturedTicks(picked, prevOddsRef.current);
        prevOddsRef.current = nextOddsMap;
        idsRef.current = ids;
        setMatches(stamped);

        if (flashTimerRef.current) {
          window.clearTimeout(flashTimerRef.current);
          flashTimerRef.current = null;
        }
        const hasFlash = stamped.some((ev) => (ev.odds || []).some((o) => o.tick));
        if (hasFlash) {
          flashTimerRef.current = window.setTimeout(() => {
            if (!alive) return;
            setMatches((curr) => curr.map((ev) => ({
              ...ev,
              odds: (ev.odds || []).map((odd) => (odd.tick ? { ...odd, tick: null } : odd)),
            })));
            flashTimerRef.current = null;
          }, FEATURED_FLASH_MS);
        }
      } catch {
        if (alive) setMatches([]);
      }
    };
    load();
    const timer = window.setInterval(load, FEATURED_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    };
  }, [locale]);

  return matches;
}

export default function WelcomeBanner({ onNavigate, onPromoDetails, onWelcomeLanding }) {
  const isMobile = useIsMobile();
  const { locale } = useLocale();
  const { homeBannerSlides: cmsSlides, loading: cmsLoading, getPromoByPromoId } = useSiteContent();
  const featured = useFeaturedFootball(locale);
  const slides = useMemo(
    () => interleaveMatchSlides(cmsSlides, featured, { showTime: 8 }),
    [cmsSlides, featured],
  );
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(() => new Set());
  const [glowColor, setGlowColor] = useState('rgba(18,20,28,0.5)');
  const total = slides.length;
  const slidesKey = useMemo(
    () => slides.map((item) => (
      item.kind === 'match'
        ? `m:${item.match?.id}`
        : `p:${item.img}|${item.showTime}|${item.ctaAction}|${item.promoId}|${item.href}`
    )).join(';;'),
    [slides],
  );

  const onSlideClick = useSlideClick({
    onNavigate,
    onPromoDetails,
    onWelcomeLanding,
    getPromoByPromoId,
  });

  const openSportEvent = useCallback((sportEvent) => {
    if (sportEvent?.gameId && sportEvent?.sportId && sportEvent?.leagueId) {
      onNavigate?.('sport', {
        sportEvent,
        sportFilter: sportEvent.type === 'live' ? 'live' : undefined,
      });
      return;
    }
    onNavigate?.('sport');
  }, [onNavigate]);

  const go = useCallback((next) => {
    setSlide(((next % total) + total) % total);
  }, [total]);

  useEffect(() => {
    if (!total) return undefined;
    const mark = (i) => setLoaded((prev) => {
      if (prev.has(i)) return prev;
      const next = new Set(prev);
      next.add(i);
      return next;
    });
    const indexes = [slide, (slide + 1) % total];
    indexes.forEach((i) => {
      const src = slides[i]?.img;
      if (!src) return;
      preloadSlideImage(src, () => mark(i));
    });
    return undefined;
  }, [slide, slides, total]);

  useEffect(() => {
    if (paused || !total) return undefined;
    const ms = (slides[slide]?.showTime || 5) * 1000;
    const t = setInterval(() => go(slide + 1), ms);
    return () => clearInterval(t);
  }, [slide, total, go, paused, slides]);

  useEffect(() => {
    if (slide >= total && total > 0) setSlide(0);
  }, [slide, total]);
  useEffect(() => {
    const item = slides[slide];
    if (!item) return;
    const url = item.kind === 'match' ? item.match?.banner_image : item.img;
    if (!url) { setGlowColor(DEFAULT_GLOW); return; }
    let alive = true;
    sampleBannerColor(url).then((col) => { if (alive) setGlowColor(col || DEFAULT_GLOW); });
    return () => { alive = false; };
  }, [slide, slides]);

  useEffect(() => {
    setSlide(0);
    setLoaded(new Set());
  }, [slidesKey]);

  if (!slides.length) {
    return cmsLoading ? <WelcomeBannerSkeleton /> : null;
  }

  if (isMobile) {
    return (
      <WelcomeBannerMobile
        slides={slides}
        loading={false}
        onSlideClick={onSlideClick}
        onSportEvent={openSportEvent}
      />
    );
  }

  const duration = slides[slide]?.showTime || 5;

  return (
    <section
      className="welcome-banner welcome-banner--photo welcome-banner--hero"
      style={{ '--banner-glow': glowColor }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="welcome-banner__glow" aria-hidden="true" />
      <div className="welcome-banner__viewport">
        <div
          className="welcome-banner__track"
          style={{ transform: `translate3d(-${slide * 100}%, 0, 0)` }}
        >
          {slides.map((item, i) => {
            if (item.kind === 'match') {
              const title = `${item.match?.home_team || ''} × ${item.match?.away_team || ''}`;
              return (
                <div
                  key={`match-${item.match?.id || i}`}
                  className="welcome-banner__slide welcome-banner__slide--match"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={title || `Match ${i + 1}`}
                >
                  <FeaturedMatchSlide
                    match={item.match}
                    locale={locale}
                    onOpen={openSportEvent}
                    onOddOpen={openSportEvent}
                  />
                </div>
              );
            }

            const title = item.title?.replace(/<[^>]+>/g, '') || '';
            const imgLoaded = loaded.has(i);
            const near = i === slide || i === (slide + 1) % total;
            const clickable = Boolean(
              item.promoId
              || item.href
              || (item.ctaAction && !['none', 'url', ''].includes(item.ctaAction)),
            );
            return (
              <div
                key={`promo-${item.img}-${i}`}
                className="welcome-banner__slide"
                role="group"
                aria-roledescription="slide"
                aria-label={title || `Slide ${i + 1}`}
              >
                <button
                  type="button"
                  className="welcome-banner__slide-inner"
                  onClick={() => onSlideClick(item)}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                >
                  {near ? (
                    <>
                      {!imgLoaded ? (
                        <div className="welcome-banner__spinner-wrap" aria-hidden="true">
                          <LoadingSpinner size="lg" />
                        </div>
                      ) : null}
                      <img
                        className={`welcome-banner__art${imgLoaded ? ' is-loaded' : ''}`}
                        src={item.img}
                        alt={title || 'Promo banner'}
                        decoding="async"
                        fetchPriority={i === slide ? 'high' : 'low'}
                        loading={i === slide ? 'eager' : 'lazy'}
                        draggable={false}
                        onLoad={() => {
                          setLoaded((prev) => {
                            if (prev.has(i)) return prev;
                            const next = new Set(prev);
                            next.add(i);
                            return next;
                          });
                        }}
                        onError={(e) => {
                          setLoaded((prev) => {
                            const next = new Set(prev);
                            next.add(i);
                            return next;
                          });
                          e.currentTarget.style.visibility = 'hidden';
                          e.currentTarget.removeAttribute('src');
                        }}
                      />
                    </>
                  ) : (
                    <div className="welcome-banner__art" aria-hidden="true" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="welcome-banner__indicators" role="group" aria-label="Promo slides">
        {slides.map((_, i) => (
          <BannerDot
            key={i === slide ? `active-${slide}` : i}
            active={i === slide}
            duration={duration}
            paused={paused}
            onClick={() => go(i)}
            label={`Slide ${i + 1}`}
            current={i === slide}
          />
        ))}
      </div>
    </section>
  );
}
